import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import LinkSection from './LinkSection';
import UserMenu from './UserMenu';
import DocumentManager from './DocumentManager';
import PasswordManager from './PasswordManager';
import DocumentationBrowser from './DocumentationBrowser';
import ApiStatusWidget from './ApiStatusWidget';

function Dashboard({ user }) {
  const [links, setLinks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [settings, setSettings] = useState({
    showApiStatus: true,
    showQuickLinks: true,
    showDocumentation: true,
    showDocumentManager: true,
    showPasswordManager: true
  });

  useEffect(() => {
    fetchLinks();
    // Load widget settings from localStorage
    const savedSettings = localStorage.getItem('hubWidgetSettings');
    if (savedSettings) {
      try {
        setSettings(JSON.parse(savedSettings));
      } catch (error) {
        console.error('Failed to parse saved settings:', error);
      }
    }
  }, []);

  const fetchLinks = async () => {
    try {
      const response = await axios.get('/api/links', { withCredentials: true });
      setLinks(response.data);
      setError(null);
    } catch (error) {
      console.error('Failed to fetch links:', error);
      setError('Failed to load links. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <h1 className="text-2xl font-bold text-forest-800">
                Seen Place Hub
              </h1>
              <span className="text-sm text-gray-500">
                Internal Portal
              </span>
            </div>
            
            <div className="flex items-center space-x-4">
              <Link
                to="/focus-groups"
                className="text-sm text-sage-600 hover:text-sage-700 font-medium"
              >
                Focus Groups
              </Link>
              <Link
                to="/sign-pdf"
                className="text-sm text-sage-600 hover:text-sage-700 font-medium"
              >
                PDF Signer
              </Link>
              <Link
                to="/admin"
                className="text-sm text-sage-600 hover:text-sage-700 font-medium"
              >
                Manage Links
              </Link>
              <Link
                to="/settings"
                className="p-2 text-sage-600 hover:text-sage-700 rounded-md hover:bg-gray-100"
                title="Settings"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </Link>
              <UserMenu user={user} />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-sage-400 border-t-transparent"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        ) : (
          <div className="space-y-8 animate-fade-in">
            {/* Quick Links */}
            {settings.showQuickLinks && links && links.sections && (
              <>
                {links.sections.map((section) => (
                  <LinkSection key={section.id} section={section} />
                ))}
              </>
            )}
            
            {/* Main services layout */}
            {settings.showDocumentation ? (
              /* When documentation is visible - documentation gets its own row */
              <div className="space-y-8">
                {/* Documentation Browser - full width */}
                <div>
                  <DocumentationBrowser />
                </div>
                
                {/* Other widgets in responsive grid below */}
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
                  {settings.showApiStatus && (
                    <div className="lg:col-span-2">
                      <ApiStatusWidget />
                    </div>
                  )}
                  
                  {settings.showDocumentManager && (
                    <div className="xl:col-span-2">
                      <DocumentManager />
                    </div>
                  )}
                  
                  {settings.showPasswordManager && (
                    <div>
                      <PasswordManager />
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* When documentation is hidden, use responsive grid */
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
                {settings.showApiStatus && (
                  <div className="lg:col-span-2">
                    <ApiStatusWidget />
                  </div>
                )}
                
                {settings.showDocumentManager && (
                  <div className="xl:col-span-2">
                    <DocumentManager />
                  </div>
                )}
                
                {settings.showPasswordManager && (
                  <div>
                    <PasswordManager />
                  </div>
                )}
              </div>
            )}
            
            {settings.showQuickLinks && !links?.sections && (
              <div className="text-center py-12 text-gray-500">
                No quick links configured
              </div>
            )}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="mt-16 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="border-t border-gray-200 pt-8 text-center text-sm text-gray-500">
            <p>Seen Place Hub • Internal Use Only</p>
            <p className="mt-1">© {new Date().getFullYear()} Seen Place</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Dashboard;